import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import {
	Box,
	Typography,
	Collapse,
	List,
	ListItemButton,
	Popover,
	CircularProgress,
	IconButton,
	InputAdornment,
} from '@mui/material'
import {
	KeyboardArrowDown,
	KeyboardArrowUp,
	Close as CloseIcon,
} from '@mui/icons-material'
import { useDebouncedCallback } from '../../hooks'
import InputCustom from '../atoms/input/InputCustom'

const canadianRegions = [
	{
		key: 'AB',
		province: 'Alberta',
		cities: [
			'Banff',
			'Brooks',
			'Calgary',
			'Edmonton',
			'Fort McMurray',
			'Grande Prairie',
			'Jasper',
			'Lake Louise',
			'Lethbridge',
			'Medicine Hat',
			'Red Deer',
			'Saint Albert',
			'More cities in Alberta',
		],
	},
	{
		key: 'BC',
		province: 'British Columbia',
		cities: [
			'Barkerville',
			'Burnaby',
			'Campbell River',
			'Chilliwack',
			'Courtenay',
			'Cranbrook',
			'Dawson Creek',
			'Delta',
			'Esquimalt',
			'Fort Saint James',
			'Fort Saint John',
			'Hope',
			'Kamloops',
			'Kelowna',
			'Kimberley',
			'Kitimat',
			'Langley',
			'Nanaimo',
			'Nelson',
			'New Westminster',
			'North Vancouver',
			'Oak Bay',
			'Penticton',
			'Powell River',
			'Prince George',
			'Prince Rupert',
			'Quesnel',
			'Revelstoke',
			'Rossland',
			'Trail',
			'Vancouver',
			'Vernon',
			'Victoria',
			'West Vancouver',
			'White Rock',
			'More cities in British Columbia',
		],
	},
	{
		key: 'MB',
		province: 'Manitoba',
		cities: [
			'Brandon',
			'Churchill',
			'Dauphin',
			'Flin Flon',
			'Kildonan',
			'Saint Boniface',
			'Swan River',
			'Thompson',
			'Winnipeg',
			'York Factory',
			'More cities in Manitoba',
		],
	},
	{
		key: 'NB',
		province: 'New Brunswick',
		cities: [
			'Bathurst',
			'Caraquet',
			'Dalhousie',
			'Fredericton',
			'Miramichi',
			'Moncton',
			'Saint John',
			'More cities in New Brunswick',
		],
	},
	{
		key: 'NL',
		province: 'Newfoundland and Labrador',
		cities: [
			'Argentia',
			'Bonavista',
			'Channel-Port aux Basques',
			'Corner Brook',
			'Ferryland',
			'Gander',
			'Grand Falls–Windsor',
			'Happy Valley–Goose Bay',
			'Harbour Grace',
			'Labrador City',
			'Placentia',
			'Saint Anthony',
			'St. John’s',
			'Wabana',
			'More cities in Newfoundland and Labrador',
		],
	},
	{
		key: 'NT',
		province: 'Northwest Territories',
		cities: [
			'Fort Smith',
			'Hay River',
			'Inuvik',
			'Tuktoyaktuk',
			'Yellowknife',
			'More cities in Northwest Territories',
		],
	},
	{
		key: 'NS',
		province: 'Nova Scotia',
		cities: [
			'Baddeck',
			'Digby',
			'Glace Bay',
			'Halifax',
			'Liverpool',
			'Louisbourg',
			'Lunenburg',
			'Pictou',
			'Port Hawkesbury',
			'Springhill',
			'Sydney',
			'Yarmouth',
			'More cities in Nova Scotia',
		],
	},
	{
		key: 'NU',
		province: 'Nunavut',
		cities: ['Iqaluit', 'More cities in Nunavut'],
	},
	{
		key: 'ON',
		province: 'Ontario',
		cities: [
			'Bancroft',
			'Barrie',
			'Belleville',
			'Brampton',
			'Brantford',
			'Brockville',
			'Burlington',
			'Cambridge',
			'Chatham',
			'Chatham-Kent',
			'Cornwall',
			'Elliot Lake',
			'Etobicoke',
			'Fort Erie',
			'Fort Frances',
			'Gananoque',
			'Guelph',
			'Hamilton',
			'Iroquois Falls',
			'Kapuskasing',
			'Kawartha Lakes',
			'Kenora',
			'Kingston',
			'Kirkland Lake',
			'Kitchener',
			'Laurentian Hills',
			'London',
			'Midland',
			'Mississauga',
			'Moose Factory',
			'Moosonee',
			'Niagara Falls',
			'Niagara-on-the-Lake',
			'North Bay',
			'North York',
			'Oakville',
			'Orillia',
			'Oshawa',
			'Ottawa',
			'Parry Sound',
			'Perth',
			'Peterborough',
			'Picton',
			'Port Colborne',
			'Saint Catharines',
			'Saint Thomas',
			'Sarnia-Clearwater',
			'Sault Sainte Marie',
			'Scarborough',
			'Simcoe',
			'Stratford',
			'Sudbury',
			'Temiskaming Shores',
			'Thorold',
			'Thunder Bay',
			'Timmins',
			'Toronto',
			'Trenton',
			'Waterloo',
			'Welland',
			'West Nipissing',
			'Windsor',
			'Woodstock',
			'York',
			'More cities in Ontario',
		],
	},
	{
		key: 'PE',
		province: 'Prince Edward Island',
		cities: [
			'Borden',
			'Cavendish',
			'Charlottetown',
			'Souris',
			'Summerside',
			'More cities in Prince Edward Island',
		],
	},
	{
		key: 'QC',
		province: 'Quebec',
		cities: [
			'Asbestos',
			'Baie-Comeau',
			'Beloeil',
			'Cap-de-la-Madeleine',
			'Chambly',
			'Charlesbourg',
			'Châteauguay',
			'Chibougamau',
			'Côte-Saint-Luc',
			'Dorval',
			'Gaspé',
			'Gatineau',
			'Granby',
			'Havre-Saint-Pierre',
			'Hull',
			'Jonquière',
			'Kuujjuaq',
			'La Salle',
			'La Tuque',
			'Lachine',
			'Laval',
			'Lévis',
			'Longueuil',
			'Magog',
			'Matane',
			'Montreal',
			'Montréal-Nord',
			'Percé',
			'Port-Cartier',
			'Quebec',
			'Rimouski',
			'Rouyn-Noranda',
			'Saguenay',
			'Saint-Eustache',
			'Saint-Hubert',
			'Sainte-Anne-de-Beaupré',
			'Sainte-Foy',
			'Sainte-Thérèse',
			'Sept-Îles',
			'Sherbrooke',
			'Sorel-Tracy',
			'Trois-Rivières',
			'Val-d’Or',
			'Waskaganish',
			'More cities in Quebec',
		],
	},
	{
		key: 'SK',
		province: 'Saskatchewan',
		cities: [
			'Batoche',
			'Cumberland House',
			'Estevan',
			'Flin Flon',
			'Moose Jaw',
			'Prince Albert',
			'Regina',
			'Saskatoon',
			'Uranium City',
			'More cities in Saskatchewan',
		],
	},
	{
		key: 'YT',
		province: 'Yukon',
		cities: ['Dawson', 'Watson Lake', 'Whitehorse', 'More cities in Yukon'],
	},
]

const LocationDropdown = ({ value, onChange }) => {
	const [anchorEl, setAnchorEl] = useState(null)
	const [expandedProvince, setExpandedProvince] = useState(null)
	const [inputValue, setInputValue] = useState(value || '')
	const [googleSuggestions, setGoogleSuggestions] = useState([])
	const [isLoading, setIsLoading] = useState(false)

	const containerRef = useRef(null)
	const sessionTokenRef = useRef(null)
	const isMounted = useRef(true)

	const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY

	useEffect(() => {
		setInputValue(value || '')
	}, [value])

	useEffect(() => {
		isMounted.current = true
		sessionTokenRef.current = Math.random().toString(36).substring(7)
		return () => (isMounted.current = false)
	}, [])

	const handleClick = (e) => {
		if (!anchorEl && containerRef.current) setAnchorEl(containerRef.current)
	}

	const handleClose = () => {
		setAnchorEl(null)
		setExpandedProvince(null)
		setGoogleSuggestions([])
		setInputValue(value || '') // restore to last selected value
	}

	const open = Boolean(anchorEl)
	const id = open ? 'location-popover' : undefined

	const handleProvinceToggle = (province) => {
		setExpandedProvince(expandedProvince === province ? null : province)
	}

	const handleSelect = (val, provinceContext = null, provinceKey = null) => {
		let finalVal = val
		if (typeof val === 'string' && val.includes('More cities'))
			finalVal =
				!!provinceKey && !!provinceContext
					? provinceKey + ', ' + provinceContext
					: !!provinceContext
						? provinceContext
						: val.replace('More cities in ', '')
		onChange?.(finalVal)
		setInputValue(finalVal)
		setAnchorEl(null)
		setExpandedProvince(null)
		setGoogleSuggestions([])
	}

	const fetchPlaceDetails = useCallback(
		async (placeId) => {
			if (!apiKey) return

			setIsLoading(true)

			try {
				const response = await fetch(
					`https://places.googleapis.com/v1/places/${placeId}`,
					{
						method: 'GET',
						headers: {
							'Content-Type': 'application/json',
							'X-Goog-Api-Key': apiKey,
							'X-Goog-FieldMask':
								'id,displayName,formattedAddress,addressComponents',
						},
					}
				)

				if (!response.ok) {
					throw new Error(`API error: ${response.status}`)
				}

				const placeData = await response.json()

				if (isMounted.current) {
					let address =
						placeData.formattedAddress ||
						placeData.displayName?.text
					const region = canadianRegions.find((r) =>
						address.includes(r.province)
					)
					if (region) address = region.key + ', ' + address
					onChange?.(address)
					setInputValue(address)
					setAnchorEl(null)
					setExpandedProvince(null)
					setGoogleSuggestions([])
					setIsLoading(false)

					sessionTokenRef.current = Math.random()
						.toString(36)
						.substring(7)
				}
			} catch (error) {
				console.error('Error fetching place details:', error)
				if (isMounted.current) {
					setIsLoading(false)
				}
			}
		},
		[apiKey, onChange]
	)

	const handleSuggestionClick = useCallback(
		(suggestion) => {
			const placeId = suggestion.placePrediction?.placeId
			if (placeId) fetchPlaceDetails(placeId)
		},
		[fetchPlaceDetails]
	)

	const handleClear = (e) => {
		e.stopPropagation()
		onChange?.('')
		setInputValue('')
		setGoogleSuggestions([])
	}

	const fetchGoogleSuggestions = useCallback(
		async (inputStr) => {
			if (!inputStr || inputStr.length < 2) {
				setGoogleSuggestions([])
				setIsLoading(false)
				return
			}

			// const isMatched = canadianRegions.some((region) => region.province.toLowerCase().includes(inputStr.toLowerCase()) || region.cities.some((city) =>city.toLowerCase().includes(inputStr.toLowerCase())))
			// if (isMatched) {setGoogleSuggestions([])setIsLoading(false)return}
			if (!apiKey) {
				setIsLoading(false)
				return
			}

			try {
				const response = await fetch(
					`https://places.googleapis.com/v1/places:autocomplete`,
					{
						method: 'POST',
						headers: {
							'Content-Type': 'application/json',
							'X-Goog-Api-Key': apiKey,
						},
						body: JSON.stringify({
							input: inputStr,
							sessionToken: sessionTokenRef.current,
							languageCode: 'en',
						}),
					}
				)

				if (!response.ok) throw new Error('API Error')
				const data = await response.json()

				if (data.suggestions) setGoogleSuggestions(data.suggestions)
				else setGoogleSuggestions([])
			} catch (err) {
				console.error('Error fetching Google Places', err)
				setGoogleSuggestions([])
			} finally {
				setIsLoading(false)
			}
		},
		[apiKey]
	)

	const debouncedFetch = useDebouncedCallback((input) => {
		fetchGoogleSuggestions(input)
	}, 500)

	const displayRegions = useMemo(() => {
		if (!inputValue || inputValue === value) return canadianRegions

		const lowerInput = inputValue.toLowerCase()
		return canadianRegions
			.map((r) => {
				const provMatch = r.province.toLowerCase().includes(lowerInput)
				const citiesMatch = r.cities.filter((c) =>
					c.toLowerCase().includes(lowerInput)
				)

				if (provMatch || citiesMatch.length > 0) {
					return {
						key: r.key,
						province: r.province,
						cities: citiesMatch.length > 0 ? citiesMatch : r.cities,
					}
				}
				return null
			})
			.filter(Boolean)
	}, [inputValue, value])

	const showGoogleResults = inputValue.length > 2 // && displayRegions.length === 0

	return (
		<Box sx={{ width: '100%' }} ref={containerRef}>
			<Box onClick={handleClick} data-cy="location-input-box">
				<InputCustom
					type="text"
					name="locationField"
					placeholder="Location"
					value={inputValue}
					required={false}
					autoComplete="off"
					data={{ locationField: inputValue }}
					setDataFunction={(newData) => {
						const val = newData.locationField || ''
						setInputValue(val)
						if (!anchorEl) setAnchorEl(containerRef.current)

						if (val.length > 2) {
							setIsLoading(true)
							debouncedFetch(val)
						} else {
							setIsLoading(false)
							setGoogleSuggestions([])
						}
					}}
					InputProps={{
						endAdornment: inputValue ? (
							<InputAdornment position="end">
								<IconButton
									size="small"
									onClick={handleClear}
									sx={{
										p: 0.25,
										backgroundColor: '#F3F4F6',
										'&:hover': {
											backgroundColor: '#E5E7EB',
										},
										mr: 1,
									}}
								>
									<CloseIcon
										sx={{ fontSize: 14, color: '#4B5563' }}
									/>
								</IconButton>
							</InputAdornment>
						) : null,
						sx: {
							// Match styling states precisely
							borderRadius: '8px',
							backgroundColor: '#fff',
							pr: '0 !important',
							pl: '0 !important',
							height: '44px',
							border: anchorEl ? '1px solid #7ca6bd' : 'none', // Handle focused visual state identically
						},
					}}
				/>
			</Box>

			<Popover
				id={id}
				open={open}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'left',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'left',
				}}
				disableAutoFocus
				disableEnforceFocus
				PaperProps={{
					sx: {
						width: anchorEl?.clientWidth || 300,
						mt: 0.5,
						borderRadius: '6px',
						boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
						overflow: 'hidden',
						border: '1px solid #E5E7EB',
					},
				}}
			>
				<Box sx={{ width: '100%' }}>
					{showGoogleResults ? (
						<Box sx={{ p: 0, maxHeight: 350, overflowY: 'auto' }}>
							{isLoading ? (
								<Box
									sx={{
										p: 3,
										display: 'flex',
										justifyContent: 'center',
									}}
								>
									<CircularProgress
										size={24}
										sx={{ color: '#0da2a9' }}
									/>
								</Box>
							) : googleSuggestions.length > 0 ? (
								<List sx={{ p: 0 }}>
									{googleSuggestions.map(
										(suggestion, index) => {
											const prediction =
												suggestion.placePrediction
											if (!prediction) return null
											const mainText =
												prediction.structuredFormat
													?.mainText?.text ||
												prediction.text?.text
											const secondaryText =
												prediction.structuredFormat
													?.secondaryText?.text

											return (
												<ListItemButton
													key={index}
													onClick={() =>
														handleSuggestionClick(
															suggestion
														)
													}
													sx={{
														py: 1.5,
														px: 2,
														display: 'flex',
														flexDirection: 'column',
														alignItems:
															'flex-start',
													}}
												>
													<Typography
														sx={{
															fontSize: '14px',
															color: '#111827',
															mb: 0.5,
														}}
													>
														{mainText}
													</Typography>
													{secondaryText && (
														<Typography
															sx={{
																fontSize:
																	'12px',
																color: '#6B7280',
															}}
														>
															{secondaryText}
														</Typography>
													)}
												</ListItemButton>
											)
										}
									)}
								</List>
							) : (
								<Box sx={{ p: 2, textAlign: 'center' }}>
									<Typography
										sx={{
											fontSize: '14px',
											color: '#6B7280',
										}}
									>
										No addresses found
									</Typography>
								</Box>
							)}
						</Box>
					) : (
						<>
							{(!inputValue || inputValue === value) && (
								<Box
									sx={{
										px: 2,
										pt: 1,
										borderBottom: '1px solid #E5E7EB',
									}}
								>
									<Box
										sx={{
											display: 'inline-block',
											pb: 1,
											borderBottom: '2px solid #0da2a9',
											color: '#111827',
											fontWeight: 600,
											fontSize: '14px',
											px: 1,
											cursor: 'pointer',
										}}
									>
										Region
									</Box>
								</Box>
							)}

							<List
								sx={{ p: 0, maxHeight: 350, overflowY: 'auto' }}
							>
								{displayRegions.map((region) => (
									<React.Fragment key={region.province}>
										<ListItemButton
											onClick={() =>
												inputValue &&
												inputValue !== value
													? handleSelect(
															region.province,
															region.province,
															region.key
														)
													: handleProvinceToggle(
															region.province
														)
											}
											sx={{
												py: 1.5,
												px: 2,
												borderBottom:
													'1px solid #F3F4F6',
												display: 'flex',
												justifyContent: 'space-between',
											}}
										>
											<Typography
												sx={{
													fontSize: '14px',
													fontWeight: 500,
													color: '#111827',
												}}
											>
												{region.province}
											</Typography>
											{expandedProvince ===
											region.province ? (
												<KeyboardArrowUp
													sx={{
														color: '#6B7280',
														fontSize: 20,
													}}
												/>
											) : (
												<KeyboardArrowDown
													sx={{
														color: '#6B7280',
														fontSize: 20,
													}}
												/>
											)}
										</ListItemButton>

										<Collapse
											in={
												expandedProvince ===
													region.province ||
												(!!inputValue &&
													inputValue !== value)
											}
											timeout="auto"
											unmountOnExit
										>
											<List
												component="div"
												disablePadding
												sx={{ bgcolor: '#FAFAFA' }}
											>
												{region.cities.map((city) => (
													<ListItemButton
														key={city}
														sx={{ pl: 4, py: 1 }}
														onClick={() =>
															handleSelect(
																city,
																region.province,
																region.key
															)
														}
													>
														<Typography
															sx={{
																fontSize:
																	'14px',
																color: '#4B5563',
															}}
														>
															{city}
														</Typography>
													</ListItemButton>
												))}
											</List>
										</Collapse>
									</React.Fragment>
								))}
							</List>
						</>
					)}
				</Box>
			</Popover>
		</Box>
	)
}

export default LocationDropdown
